<template>
  <v-btn
    :disabled="disabled"
    :color="color"
    :loading="loading"
    :class="classes"
    :type="type"
    :size="mappedSize"
    :form="form"
    :to="to"
    :append-icon="iconPosition === 'end' ? icon : undefined"
    :prepend-icon="iconPosition === 'start' ? icon : undefined"
    :variant="outlined ? 'outlined' : 'flat'"
    v-bind="$attrs"
  >
    <template v-if="$slots.loader" #loader><slot name="loader" /></template>
    <slot />
  </v-btn>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

import type { IButtonProps, IButtonVariants } from './ui-button-types';

type Props = IButtonProps;

const props = withDefaults(defineProps<Props>(), {
  icon: undefined,
  iconPosition: 'start',
  loading: false,
  disabled: false,
  variant: 'primary',
  type: 'button',
  size: 'md',
  outlined: false,
  narrow: false,
  to: undefined,
  form: undefined,
});

const mappedSize = computed(() => {
  if (props.size === 'sm') {
    return 'small';
  }

  if (props.size === 'lg') {
    return 'large';
  }

  return 'default';
});

const classes = computed(() => {
  const nextClasses: string[] = [`ui-button-variant-${props.variant}`];

  if (props.variant === 'primary' && !props.outlined)
    nextClasses.push('text-white');
  if (props.variant === 'secondary') nextClasses.push('text-primary');

  if (props.variant === 'danger-clear') {
    nextClasses.push('text-error', 'bg-white');
    if (props.outlined) nextClasses.push('border border-current');
  }

  if (props.variant === 'danger') nextClasses.push('bg-error text-white');
  if (props.variant === 'warning') nextClasses.push('bg-warning text-white');
  if (props.variant === 'success')
    nextClasses.push('bg-app-success text-white');

  if (props.variant === 'clear') {
    nextClasses.push('ui-button-clear');
    nextClasses.push(props.disabled ? 'clear-bg' : 'text-primary');
  }

  if (props.variant === 'black-clear') {
    nextClasses.push('ui-button-clear', 'black--text');
  }

  const filledVariants: IButtonVariants[] = [
    'primary',
    'secondary',
    'danger',
    'warning',
    'success',
  ];
  if (filledVariants.includes(props.variant)) {
    nextClasses.push('shadow-elevated');
  }

  if (props.narrow) nextClasses.push('ui-button-narrow');
  if (props.outlined) nextClasses.push('ui-button-outlined');

  nextClasses.push(`ui-button-${props.size}`);
  return nextClasses;
});

const color = computed(() => {
  return ['clear'].includes(props.variant) ? '' : props.variant;
});
</script>

<style scoped lang="scss">
.ui-button-clear {
  background-color: theme('colors.white');
}

:deep(.v-icon) {
  color: currentColor !important;
}

.btn-icon-spacer {
  margin-right: 0.5em;
  color: currentColor !important;
}

.ui-button-narrow {
  &.v-btn:not(.v-btn--round).v-btn--size-default {
    --v-btn-height: 44px;
    padding: 6px;
    min-width: 44px;
    min-height: 44px;

    :deep(.v-icon) {
      transform: scale(1.36);
    }
  }
}

.ui-button-lg {
  --v-btn-height: 40px;
}

:deep(.v-btn__content) {
  position: relative;
  max-height: 100%;
  white-space: normal !important;
  line-height: 1;
  text-wrap: wrap;
  height: auto;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

:deep(.v-btn__content > *) {
  max-height: 100%;
  white-space: normal !important;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
}

// Targets the root v-btn element directly.
// :deep(.v-btn) compiles to [data-v-xxx] .v-btn which only matches
// descendants — not the root element itself. Using .v-btn compiles to
// .v-btn[data-v-xxx] which correctly matches the root button.
.v-btn {
  height: auto !important;
  min-height: var(--v-btn-height);
  white-space: normal;
}

.clear-bg :deep(.v-btn__overlay) {
  background: none;
}
</style>
